import type { ZxcvbnResult } from '@zxcvbn-ts/core';
import { Message } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const usePasswordValidator = () => {
    const { t } = useTranslation();

    const validatePassword = async (value: string | null | undefined): Promise<ZxcvbnResult> => {
        const zxcvbnCommonPackage = await import('@zxcvbn-ts/language-common');
        const zxcvbnEnPackage = await import('@zxcvbn-ts/language-en');
        const { ZxcvbnFactory } = await import('@zxcvbn-ts/core');
        const options = {
            dictionary: {
                ...zxcvbnCommonPackage.dictionary,
                ...zxcvbnEnPackage.dictionary
            },
            graphs: zxcvbnCommonPackage.adjacencyGraphs,
            translations: zxcvbnEnPackage.translations
        };
        const zxcvbn = new ZxcvbnFactory(options);
        return zxcvbn.check(value ?? '');
    };

    return {
        validatePassword,
        passwordValidation: {
            required: t('Validation.Password.Required'),
            validate: async (value: string | null | undefined): Promise<Message | Message[] | boolean | undefined> => {
                const result = await validatePassword(value ?? '');
                return result.score >= 3;
            }
        },
        /**
         * The password field's message (spread on its `Input`): the strength while typing (weak = error, strong = info),
         * announced as a polite status, and otherwise the form's validation error, e.g. "required" on an empty submit.
         * `score` is -1 while there is no password; a weak password's validation error has an empty message.
         */
        passwordMessages: (score: number, validationError?: string) => {
            const strength = score >= 0 ? t(`Validation.Password.ComplexityScore${score}`) : undefined;
            return {
                errorMessage: score >= 3 ? undefined : strength || validationError || undefined,
                infoMessage: score >= 3 ? strength : undefined,
                announceMessage: true
            };
        }
    };
};
